import * as React from 'react';
import { useSelector } from 'react-redux';
import { flatMap } from 'lodash';
import Select from 'antd/lib/select';
import Spin from 'antd/lib/spin';
import { getIssues } from '@/api';
import { IssueEntity, IssueSourceRef } from '@/types';
import { RootState } from '@/reducers/reducers';
import { selectAppTimeFormatSetting, selectAuthCredentials, selectEffectiveTimezone } from '@/selectors/selectors';
import { formatDateWithTimezone } from '@/timeHelpers';
import { issueSourceDisplayText, sourceOptionLabel } from './triageHelpers';

const PAGE_SIZE = 50;

// Which candidate pool the select queries:
// - AbsorbableSources: individual sources of open issues without a fix request, each pullable into another issue.
// - OpenIssues: whole open issues, used to pick issues to mark as related.
export enum IssueCandidatesKind {
    AbsorbableSources = 'absorbableSources',
    OpenIssues = 'openIssues',
}

export const ATTACH_SIGNALS_TOOLTIP = (
    <>
        <p>
            The signal will be associated with this issue instead, and its original issue will be deleted if there were
            no other signals associated.
        </p>
        <p style={{ marginBottom: 0 }}>To add a signal already associated with a fix, unmerge it first.</p>
    </>
);

const SOURCE_REF_SEPARATOR = '|';

const sourceRefKey = (issueId: string, sourceId: string) => `${issueId}${SOURCE_REF_SEPARATOR}${sourceId}`;

const parseSourceRefKey = (key: string): IssueSourceRef => {
    const [issueId, sourceId] = key.split(SOURCE_REF_SEPARATOR);
    return { issueId, sourceId };
};

export function useIssueCandidates(
    orgId: string,
    appId: string,
    open: boolean,
    kind: IssueCandidatesKind,
    excludeSet: Set<string>,
) {
    const auth = useSelector((state: RootState) => selectAuthCredentials(state));
    const [candidates, setCandidates] = React.useState<IssueEntity[]>([]);
    const [isLoading, setIsLoading] = React.useState(false);
    const [isLoadingMore, setIsLoadingMore] = React.useState(false);
    const [hasMore, setHasMore] = React.useState(false);
    const [search, setSearch] = React.useState('');
    const offsetRef = React.useRef(0);
    // Each fetch gets a sequence number; a response is dropped if a newer request was issued meanwhile.
    const requestIdRef = React.useRef(0);
    const inFlightRef = React.useRef(false);

    const fetchPage = React.useCallback(
        async (reset: boolean) => {
            if (!reset && inFlightRef.current) {
                return;
            }
            const requestId = ++requestIdRef.current;
            inFlightRef.current = true;
            const offset = reset ? 0 : offsetRef.current;
            if (reset) {
                setIsLoading(true);
            } else {
                setIsLoadingMore(true);
            }
            const isCurrent = () => requestId === requestIdRef.current;
            try {
                const res = await getIssues(
                    {
                        orgId,
                        appId,
                        statusGroup: 'open',
                        withoutFixRequest: kind === IssueCandidatesKind.AbsorbableSources || undefined,
                        search: search || undefined,
                        // Options are one row per source, so only the sources that match should come back.
                        searchSources: kind === IssueCandidatesKind.AbsorbableSources || undefined,
                    },
                    PAGE_SIZE,
                    offset,
                    auth,
                );
                if (!isCurrent()) {
                    return;
                }
                const page = res.issues.filter((issue) => !excludeSet.has(issue.id));
                setCandidates((prev) => (reset ? page : [...prev, ...page]));
                offsetRef.current = offset + res.issues.length;
                setHasMore(res.issues.length === PAGE_SIZE);
            } catch {
                if (!isCurrent()) {
                    return;
                }
                if (reset) {
                    setCandidates([]);
                }
                setHasMore(false);
            } finally {
                if (isCurrent()) {
                    inFlightRef.current = false;
                    setIsLoading(false);
                    setIsLoadingMore(false);
                }
            }
        },
        [orgId, appId, kind, excludeSet, auth, search],
    );

    React.useEffect(() => {
        if (!open) {
            return;
        }
        offsetRef.current = 0;
        setCandidates([]);
        setHasMore(false);
        fetchPage(true);
    }, [open, fetchPage]);

    const loadMore = () => {
        if (hasMore && !inFlightRef.current) {
            fetchPage(false);
        }
    };

    return { candidates, isLoading, isLoadingMore, hasMore, loadMore, setSearch };
}

interface IssueCandidatesSelectProps {
    orgId: string;
    appId: string;
    open: boolean;
    kind: IssueCandidatesKind;
    excludeIssueIds?: string[];
    value: string[];
    onChange: (values: string[]) => void;
    placeholder?: string;
    notFoundText?: string;
}

export const AbsorbableSourcesSelect: React.FC<
    Omit<IssueCandidatesSelectProps, 'kind' | 'value' | 'onChange'> & {
        value: IssueSourceRef[];
        onChange: (refs: IssueSourceRef[]) => void;
    }
> = ({ value, onChange, ...props }) => (
    <IssueCandidatesSelect
        {...props}
        kind={IssueCandidatesKind.AbsorbableSources}
        value={value.map((ref) => sourceRefKey(ref.issueId, ref.sourceId))}
        onChange={(keys) => onChange(keys.map(parseSourceRefKey))}
    />
);

export const IssueCandidatesSelect: React.FC<IssueCandidatesSelectProps> = ({
    orgId,
    appId,
    open,
    kind,
    excludeIssueIds,
    value,
    onChange,
    placeholder = 'Select or search for existing signals',
    notFoundText = 'No signals available to pull in',
}) => {
    // Keyed on the joined ids so a new array instance per render doesn't retrigger fetches.
    const excludeKey = (excludeIssueIds ?? []).join(',');
    const excludeSet = React.useMemo(() => new Set(excludeKey ? excludeKey.split(',') : []), [excludeKey]);
    const { candidates, isLoading, isLoadingMore, hasMore, loadMore, setSearch } = useIssueCandidates(
        orgId,
        appId,
        open,
        kind,
        excludeSet,
    );
    const effectiveTimezone = useSelector((state: RootState) => selectEffectiveTimezone(state, appId));
    const use24HourTimeFormat = useSelector((state: RootState) => selectAppTimeFormatSetting(state, appId));
    // Debounce the server-side search so we don't fire a request per keystroke.
    const searchTimer = React.useRef<ReturnType<typeof setTimeout>>();
    React.useEffect(() => () => clearTimeout(searchTimer.current), []);
    const onSearch = (input: string) => {
        clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => setSearch(input.trim()), 300);
    };
    const onDropdownVisibleChange = (visible: boolean) => {
        if (!visible) {
            clearTimeout(searchTimer.current);
            setSearch('');
        }
    };
    const onPopupScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const target = e.currentTarget;
        if (hasMore && !isLoadingMore && target.scrollTop + target.offsetHeight >= target.scrollHeight - 32) {
            loadMore();
        }
    };
    const options =
        kind === IssueCandidatesKind.OpenIssues ?
            candidates.map((issue) => {
                const text = issue.title || issue.description || 'Untitled issue';
                return {
                    value: issue.id,
                    label: sourceOptionLabel(issue.sources?.[0], appId, text),
                };
            })
        :   flatMap(candidates, (issue) =>
                (issue.sources ?? []).map((source) => ({
                    value: sourceRefKey(issue.id, source.id),
                    label: sourceOptionLabel(
                        source,
                        appId,
                        issueSourceDisplayText(source),
                        formatDateWithTimezone(
                            source.createdAt,
                            'shortWithTimezone',
                            effectiveTimezone,
                            use24HourTimeFormat,
                        ),
                    ),
                })),
            );
    return (
        <Select
            mode="multiple"
            allowClear
            loading={isLoading}
            value={value}
            onChange={onChange}
            options={options}
            filterOption={false}
            onSearch={onSearch}
            onDropdownVisibleChange={onDropdownVisibleChange}
            onPopupScroll={onPopupScroll}
            placeholder={placeholder}
            style={{ width: '100%' }}
            notFoundContent={isLoading ? <Spin size="small" /> : notFoundText}
            dropdownRender={(menu) => (
                <>
                    {menu}
                    {isLoadingMore && (
                        <div style={{ textAlign: 'center', padding: 8 }}>
                            <Spin size="small" />
                        </div>
                    )}
                </>
            )}
        />
    );
};
